import { z } from '@4players/odin-common/zod';
import { createStore } from '@ngneat/elf';
import {
  addEntities,
  deleteAllEntities,
  deleteEntities,
  hasEntity,
  selectAllEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
} from 'rxjs';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CHAT_RETENTION_MS, UserSettingsStore } from './user-settings.repository';
import { MessageReadStateStore } from './message-read-state.repository';
import { createIdbStorage } from './persistence/idb-storage';
import { tenantStorageKey } from '../shared/helpers/tenant';
import { isImageDataUrl } from '../shared/helpers/image-data-url';
import { ElementTypeEnum, WhiteboardElement } from '../shared/components/ng-whiteboard';

export const messageKindSchema = z.union([
  z.literal('poke'),
  z.literal('message'),
  z.literal('mute'),
  z.literal('whiteboard'),
  z.literal('whiteboard-clear'),
  z.literal('talkPermission'),
  // Wire-only "interaction" kind — never stored as its own row. Handlers in
  // ChatService mutate an already-stored target message identified by
  // `payload.targetMessageId`. Full recipe:
  // ../../../docs/agent-reference/chat.md#pattern-target-mutating-message-kinds.
  z.literal('reaction'),
  // A file shared in the room (see `RoomFilesService`): rendered as a file
  // card with a download button. The file itself lives on the server; the
  // card resolves `payload.fileId` against the room's live file list.
  z.literal('file'),
]);

/**
 * The scope the message is within. The recipient knows the sender, but without
 * this extra info they cannot tell how the message should be displayed — it
 * could be a private message, a broadcast to all users in a room or a system message.
 *
 */
export const scopeSchema = z
  .union([z.literal('room'), z.literal('private'), z.literal('system')])
  .default('room');

export type Scope = z.infer<typeof scopeSchema>;

/**
 * Messages are persisted in IndexedDB. Store the user details needed to display
 * history alongside stable user IDs, since a participant may no longer be
 * available in the live user store when that history is displayed.
 */

// Every wire and locally-generated message carries a UUID that identifies it
// across clients. Senders mint the id before transmission so the same logical
// message has the same id everywhere — required for reactions, replies and
// future target-mutating interactions. Locally-generated drafts (system
// notices) get an id from `MessageStore.add()` when they don't supply one.
const baseMessageSchema = z.object({
  id: z.string().uuid(),
  scope: scopeSchema,
  kind: messageKindSchema,
});

const textMessageTransferImageAttachmentSchema = z.object({
  type: z.literal('image'),
  // Senders inline the (compressed) image as a data URL; any other URL would
  // make every recipient's browser fetch it (see `isImageDataUrl`).
  data: z.string().refine(isImageDataUrl),
  // Intrinsic pixel dimensions of the (post-compression) image. Senders
  // capture these from a temp Image element after compression so the
  // values match what `data` decodes to. Receivers feed them straight to
  // the `<img width height>` attrs so the browser reserves the aspect-
  // ratio box before the image data lands — without this the row's height
  // grows when the image decodes, which mid-scroll shows up as a visible
  // jump (or, for scrollbar drag, fights the user's drag = rubberband).
  // Optional because legacy persisted/wire messages from older clients
  // didn't carry these fields. The renderer falls back to its usual CSS
  // max-width / max-height behavior when absent.
  width: z.optional(z.number()),
  height: z.optional(z.number()),
});

// Self-contained reply preview that travels with the new message. The `id`
// lets the recipient scroll-to-and-flash the original if they have it locally;
// the snippet + senderName keep the preview meaningful when they don't (late
// joiners, pruned history, cross-scope replies). Snippet is capped at 200
// chars; for image-only originals the snippet is the attachment's alt-text or
// the literal `[Image]`.
const quotedMessageSchema = z.object({
  id: z.string().uuid(),
  senderName: z.string(),
  textSnippet: z.string().max(200),
});

export type QuotedMessage = z.infer<typeof quotedMessageSchema>;

// Wire schema for text messages — strictly what senders ship over Odin.
// Receiver-derived fields (`embedUrl`) deliberately live on
// the *storage* shape (`TextMessagePayload`) below, not here, so:
//   1. A malicious or out-of-date peer can't inject a spoofed embed card
//      or a fake markdown source — Zod's default `.strip()` discards any
//      extra fields at parse time.
//   2. Per-receiver state (which OGP backend resolved a link, what render
//      pipeline produced the HTML) doesn't have to round-trip through the
//      wire, where it would be wasted bandwidth and ambiguous authority.
//   3. The category boundary is explicit: wire = sender intent, storage =
//      wire + receiver-derived enrichments.
const textMessageWirePayloadSchema = z.object({
  // Raw markdown source the sender typed. Stored as-is; rendering to
  // HTML happens at display time in `ChatMsg` via a memoized
  // computed signal (see `shared/helpers/message-html.ts`).
  text: z.optional(z.string()),
  // An invalid attachment is dropped on its own so the text still arrives.
  attachment: z.optional(textMessageTransferImageAttachmentSchema).catch(undefined),
  quotedMessage: z.optional(quotedMessageSchema),
});

// Public sender-side type — what code constructing an outgoing message
// builds. Storage consumers should use `TextMessagePayload` below
// (which extends this with receiver-derived fields).
export type TextMessageWirePayload = z.infer<typeof textMessageWirePayloadSchema>;

// Storage type — wire payload + receiver-derived fields populated by
// `EmbedResolverService` (embedUrl). `payload.text` on a stored message
// holds the raw markdown source (same as on the wire); HTML rendering
// happens at display time.
export interface TextMessagePayload extends TextMessageWirePayload {
  // OGP preview card. Populated locally by `EmbedResolverService` after
  // receive — never accepted from the wire.
  embedUrl?: {
    title: string;
    href: string;
    description?: string;
    image?: string;
    // Intrinsic pixel dimensions of `image`, captured by the resolver
    // after OGP returns. Renderer applies them to `<img width height>`
    // so the browser reserves the aspect-ratio box before the (often
    // base64-encoded, sometimes ~MB-sized) image decodes — without
    // these, every embed-bearing row reflows when its image lands,
    // which during a scrollbar drag presents as visible rubberband.
    imageWidth?: number;
    imageHeight?: number;
  };
}

const textMessageTransferSchema = baseMessageSchema.extend({
  kind: z.literal('message'),
  payload: textMessageWirePayloadSchema,
});

const pokeMessageTransferSchema = baseMessageSchema.extend({
  kind: z.literal('poke'),
  payload: z.string(),
});

const muteMessageTransferSchema = baseMessageSchema.extend({
  kind: z.literal('mute'),
  payload: z.string(),
});

// Paint values (`fill` / `stroke`) a peer's whiteboard element may carry: hex,
// a keyword, or a functional color such as `rgb(var(--text-color))`. Never
// `url(…)` — Firefox resolves paint references against external documents.
const whiteboardPaintSchema = z
  .string()
  .regex(/^(#[0-9a-f]{3,8}|[a-z]*|(rgba?|hsla?)\([\w\s,.%()-]*\))$/i);

// One element of a peer's whiteboard layer. Constrains only what the renderer
// feeds into resource-loading attributes (the image `href`, paint values) and
// the fields it does arithmetic on; the remaining geometry passes through.
const whiteboardElementSchema = z
  .looseObject({
    id: z.string(),
    type: z.enum(ElementTypeEnum),
    value: z.string(),
    x: z.number().catch(0),
    y: z.number().catch(0),
    rotation: z.number().catch(0),
    opacity: z.number().catch(100),
    options: z
      .looseObject({
        strokeColor: whiteboardPaintSchema.optional(),
        fill: whiteboardPaintSchema.optional(),
        color: whiteboardPaintSchema.optional(),
      })
      .default({}),
  })
  .refine((element) => element.type !== ElementTypeEnum.IMAGE || isImageDataUrl(element.value));

// A peer's whole layer. Invalid elements are dropped one by one rather than
// discarding everything the peer drew.
const whiteboardElementsSchema = z.array(z.unknown()).transform((items) =>
  items.flatMap((item) => {
    const parsed = whiteboardElementSchema.safeParse(item);
    return parsed.success ? [parsed.data as WhiteboardElement] : [];
  })
);

const whiteboardMessageTransferSchema = baseMessageSchema.extend({
  kind: z.literal('whiteboard'),
  payload: whiteboardElementsSchema,
});

const whiteboardClearMessageTransferSchema = baseMessageSchema.extend({
  kind: z.literal('whiteboard-clear'),
  payload: z.any(),
});

const talkPermissionPayloadSchema = z.object({
  direction: z.enum(['request', 'response']),
  isAccepted: z.boolean(),
  message: z.string(),
});

export type TalkPermissionPayload = z.infer<typeof talkPermissionPayloadSchema>;

const talkPermissionMessageTransferSchema = baseMessageSchema.extend({
  kind: z.literal('talkPermission'),
  payload: talkPermissionPayloadSchema,
});

// Wire-only "target-mutating" payload — see
// ../../../docs/agent-reference/chat.md#pattern-target-mutating-message-kinds.
// `targetMessageId` is the canonical field
// name and `'add' | 'remove'` the canonical verb pair across all interaction
// kinds (today reactions; future poll votes will follow the same shape).
const reactionPayloadSchema = z.object({
  targetMessageId: z.string().uuid(),
  emoji: z.string(),
  action: z.union([z.literal('add'), z.literal('remove')]),
});

export type ReactionPayload = z.infer<typeof reactionPayloadSchema>;

// Announces a file shared in the room. Sent by the uploader once the server
// holds the complete file; the file list itself syncs over the realtime
// websocket, so a card whose file has since expired or been deleted shows as
// no longer available. Older clients drop the unknown kind.
const filePayloadSchema = z.object({
  fileId: z.string().min(1).max(64),
  name: z.string().max(255),
  size: z.number().int().nonnegative(),
  mime: z.string().max(255),
  encrypted: z.boolean(),
});

export type FileMessagePayload = z.infer<typeof filePayloadSchema>;

const fileMessageTransferSchema = baseMessageSchema.extend({
  kind: z.literal('file'),
  payload: filePayloadSchema,
});

const reactionMessageTransferSchema = baseMessageSchema.extend({
  kind: z.literal('reaction'),
  payload: reactionPayloadSchema,
});

export const messageTransferSchema = z.union([
  textMessageTransferSchema,
  pokeMessageTransferSchema,
  muteMessageTransferSchema,
  whiteboardMessageTransferSchema,
  whiteboardClearMessageTransferSchema,
  talkPermissionMessageTransferSchema,
  reactionMessageTransferSchema,
  fileMessageTransferSchema,
]);

export type MessageTransfer = z.infer<typeof messageTransferSchema>;

// One reaction entry — keyed on `(userId, emoji)` for dedup. `userId` is the
// stable application userId (not the per-session peerId) so reaction state
// survives reconnects. See ../../../docs/agent-reference/chat.md#field-name-discipline.
export type Reaction = {
  emoji: string;
  userId: string;
  time: Date;
};

export type MessageBase = {
  id: string;
  sender: {
    id: string;
    name: string;
    avatar: string;
  };
  receiver?: {
    id: string;
    name: string;
    avatar: string;
  };
  time: Date;
  scope: Scope;
  // The room the message was received in — sourced from
  // `UserSettingsStore.roomId` at the time of add (which matches the room
  // currently joined via `OdinService`). Used for display-time filtering
  // (`userSettings.roomId === message.roomId`); retention pruning is global
  // by `time` and doesn't consult it.
  roomId: string;
  // The breakoutRoomId where the user was in when the message was received. This is currently
  // user.data.breakoutRoomId. If it's an empty string (''), the message is not restricted to a
  // specific breakoutRoomId (message should be displayed regardless of the breakoutRoomId).
  breakoutRoomId: string;
  origin: 'self' | 'system' | 'remote';
  // Whether this message should contribute to the unread counter / badge.
  // When omitted: user messages (`origin === 'self' | 'remote'`) notify; system
  // messages (`origin === 'system'`) do not. Set explicitly (`true` on a
  // system message, `false` on a user message) to override the default.
  // Own messages never notify regardless of this flag.
  notifies?: boolean;
  // Reactions attached to this message. Wire-only `kind: 'reaction'` messages
  // mutate this field via the target-mutating handler in ChatService; reaction
  // wire-messages themselves are never stored as rows.
  reactions?: Reaction[];
};

type MessageBundle = {
  isBundled?: boolean;
  isFirstInBundle?: boolean;
  isLastInBundle?: boolean;
};

// v2 → v3 when the wire schema gained `id` (sender-assigned UUID) plus per-
// message `reactions[]` and `payload.quotedMessage` for replies. Stale
// versions are dropped on first load by `persistState` because the key no
// longer matches.
// v3 → v4 forces a one-time cleanup of legacy data on tester reload.
// The schema change itself (added optional `embedUrl.imageWidth` /
// `embedUrl.imageHeight`) is non-breaking, but legacy v3 messages carry
// embed images and image attachments without dims — and those are what
// produce the visible scrollbar-drag rubberband (rows reflow as their
// images decode mid-drag). Wiping v3 on update is simpler than a
// per-message backfill migration and matches the "few testers, dev
// pre-release" stage of the app.
// v4 → v5: `payload.text` used to carry rendered HTML (receive-time
// markdown render). It now carries the raw markdown source — rendering
// moved to display time. v4 messages would render their stored HTML
// back through the markdown pipeline (HTML inside HTML, garbage out),
// so wipe.
// v5 → v6: presence payload's `peerId: number` renamed to `userId: string`
// (stable across reconnects). Legacy v5 entries are dropped on first load
// because the key no longer matches.
// v6 → v7: `kind: 'presence'` removed from the message union — presence/
// breakout activity moved to its own `ActivityLogStore` (see
// `state/activity-log.repository.ts`). Legacy v6 blobs may still contain
// presence rows that would no longer parse under the trimmed union, so
// bumping the key cleanly drops them on first load.
const MESSAGES_STORE_NAME = 'andorMessages.v7';
// Messages moved off localStorage to IDB because image attachments blow the
// shared origin quota quickly. User settings and read-state remain on
// localStorage — they're tiny and benefit from the sync API. The adapter stores
// every key under a single `kv` object store inside the `andor` database.
const idbStorage = createIdbStorage('andor');

// Distributive Omit: preserves the `kind`-discriminated union of Message.
// Plain `Omit<Message, 'id'>` collapses the union into a single flattened
// type, which breaks `Extract<…, { kind: 'poke' | 'mute' }>` at call sites
// like ChatService.buildNotification. Distribution happens only when the
// checked type is a naked type parameter, so we route through `T`.
type DistributiveOmit<T, K extends keyof any> = T extends unknown ? Omit<T, K> : never;

// Swaps the wire payload for the storage payload on the text-kind branch
// of a `MessageTransfer` union member. Other kinds (poke / mute / etc.)
// pass through unchanged — they have no receiver-derived enrichments.
// Distributes over the union so each kind is handled in isolation.
type WithStoragePayload<T> = T extends { kind: 'message' }
  ? DistributiveOmit<T, 'payload'> & { payload: TextMessagePayload }
  : T;

// Stored shape of a message — wire shape with receiver-derived fields
// (`embedUrl` on the text branch) layered onto via `WithStoragePayload`,
// plus the local-only enrichments from `MessageBase` (id, sender, time,
// room context, reactions) and `MessageBundle` (computed grouping flags).
export type Message = WithStoragePayload<MessageTransfer> & MessageBase & MessageBundle;

// Shape of a message before it hits the store. `id` is optional here so:
// - wire-received messages can pass through the sender-assigned id unchanged
//   (ChatService.buildMessage forwards `messageData.id`),
// - locally-generated drafts (raise-hand / talk-permission notices) can omit it and
//   MessageStore.add() will mint one.
export type MessageDraft = DistributiveOmit<Message, 'id'> & { id?: string };

const messageStore = createStore({ name: MESSAGES_STORE_NAME }, withEntities<Message>());

// Unread rule lives here so any component rendering a message shares the same
// truth with the unread counter. Own messages are never "unread". `notifies`
// can opt a message in or out explicitly; otherwise system messages default to
// silent and user messages default to notifying.
export function isUnread(message: Message, lastReadAt: Readonly<Record<string, number>>): boolean {
  if (message.origin === 'self') return false;
  const notifies = message.notifies ?? message.origin !== 'system';
  if (!notifies) return false;
  const watermark = lastReadAt[message.roomId] ?? 0;
  return message.time.getTime() > watermark;
}

// Time-based retention: drop every message older than the cutoff, across all
// rooms in one pass. No sort needed — expiry is an absolute predicate on
// `time`. Returning the same state reference on no-op is load-bearing: elf
// skips the emission, so per-add prunes cause no extra IDB writes.
function pruneExpired(cutoff: number) {
  return (state: { ids: string[]; entities: Record<string, Message> }, context: unknown) => {
    const toDelete = state.ids.filter((id) => {
      const message = state.entities[id];
      return message !== undefined && message.time.getTime() < cutoff;
    });
    if (toDelete.length === 0) return state;
    return deleteEntities(toDelete)(state as never, context as never);
  };
}

@Injectable({
  providedIn: 'root',
})
export class MessageStore {
  store = messageStore;
  // Fires `true` once rehydration from IDB has completed (or immediately when
  // persistence is disabled). Consumers that inspect the raw `store.getValue()`
  // at startup (e.g. ChatService's read-state seeding) must wait on this.
  initialized$ = new BehaviorSubject<boolean>(false);
  // Gated on `initialized$` rather than filtering out empty arrays: nothing
  // emits until rehydration lands (no empty-placeholder flash during startup),
  // but a deliberate `clear()` afterwards does propagate its empty state to
  // subscribers (chat lists reset without a reload).
  messages$ = combineLatest([messageStore.pipe(selectAllEntities()), this.initialized$]).pipe(
    filter(([, ready]) => ready),
    map(([messages]) => messages)
  );
  // Unread count scoped to the currently joined room. The chat can only ever
  // display the current room's messages, so unreads left behind in other rooms
  // must not surface on the badge — it could never be cleared by opening the
  // chat. Gated on `initialized$` like `messages$`, so a message arriving
  // before rehydration lands can't be counted while the chat renders nothing.
  // Emits 0 when empty.
  currentRoomUnreadCount$: Observable<number>;
  private _persistHandle?: { unsubscribe(): void; initialized$: Observable<boolean> };

  constructor(
    private _userSettings: UserSettingsStore,
    private _readState: MessageReadStateStore
  ) {
    this.currentRoomUnreadCount$ = combineLatest([
      messageStore.pipe(selectAllEntities()),
      this._readState.lastReadAt$,
      this._userSettings.roomId$,
      this.initialized$,
    ]).pipe(
      filter(([, , , ready]) => ready),
      map(
        ([messages, lastReadAt, roomId]) =>
          messages.filter((m) => m.roomId === roomId && isUnread(m, lastReadAt)).length
      )
    );

    // Retention doubles as the persistence switch: 'off' = persistence off.
    // The initial emission wires up persistence on first construction; subsequent
    // emissions toggle persistence / retroactively apply the new cutoff.
    this._userSettings.chatRetention$
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((retention) => {
        if (retention === 'off') {
          this._disablePersistence();
        } else {
          this._enablePersistence();
          this._pruneExpired();
        }
      });
    // The message and settings stores are module-scoped and outlive this
    // instance: a re-created injector (each TestBed spec) must not leave its
    // IDB writer behind. Only detach it — the persisted history stays.
    inject(DestroyRef).onDestroy(() => this._persistHandle?.unsubscribe());
  }

  has(id: string): boolean {
    return messageStore.query(hasEntity(id));
  }

  // The set of `roomId`s present in the store right now. Callers that
  // need to act per-room without already knowing the full room set use
  // this — primarily `MessageReadStateStore.markAllAsRead`, which needs
  // an explicit room list (its own watermark map can't enumerate rooms
  // that have never been written to it).
  knownRoomIds(): Set<string> {
    const state = this.store.getValue();
    const rooms = new Set<string>();
    for (const id of state.ids) {
      const m = state.entities[id as string];
      if (m) rooms.add(m.roomId);
    }
    return rooms;
  }

  // Preserve sender-assigned IDs on wire messages and their local echoes.
  // Generate an ID only when a local draft omits it; callers keep the returned
  // message for later enrichment or upsert without changing its identity.
  add(draft: MessageDraft): Message {
    const message = { ...draft, id: draft.id ?? crypto.randomUUID() } as Message;
    const retention = this._userSettings.value.chatRetention;
    this.store.update(
      (store, context) => {
        if (store.ids.length === 0) return addEntities(message)(store, context);
        const previous = store.entities[store.ids.at(-1)!];

        if (
          previous.sender.id !== message.sender.id ||
          previous.kind !== message.kind ||
          previous.scope !== message.scope ||
          previous.receiver?.id !== message.receiver?.id
        ) {
          return addEntities(message)(store, context);
        }

        // only bundle message when they were send within 60 seconds after the previous one
        if (message.time.getTime() - previous.time.getTime() > 60000) {
          return addEntities(message)(store, context);
        }

        if (!previous.isBundled) previous.isFirstInBundle = true;
        previous.isBundled = true;
        previous.isLastInBundle = false;

        message.isBundled = true;
        message.isLastInBundle = true;

        store.entities[store.ids.at(-1)!] = previous;

        return addEntities(message)(store, context);
      },
      // 'off' means "unbounded in-memory, no persistence" — skip pruning.
      ...(retention !== 'off' ? [pruneExpired(Date.now() - CHAT_RETENTION_MS[retention])] : [])
    );
    return message;
  }

  upsert(message: Message): void {
    this.store.update(upsertEntities(message));
  }

  // Used when a presence-merge migration empties a slot's `users` array — the
  // resulting message would render as "no one joined the room", so drop it.
  delete(id: string): void {
    this.store.update(deleteEntities(id));
  }

  clear(): void {
    // `deleteAllEntities()` rather than `messageStore.reset()` so the change
    // propagates through `selectAllEntities()` subscribers (chat lists). Reset
    // updates the underlying state container, but the entity selector tracks
    // ids/entities changes and reset doesn't always fan out cleanly to it.
    messageStore.update(deleteAllEntities());
  }

  private _pruneExpired(): void {
    const retention = this._userSettings.value.chatRetention;
    if (retention === 'off') return;
    this.store.update(pruneExpired(Date.now() - CHAT_RETENTION_MS[retention]));
  }

  private _enablePersistence(): void {
    if (this._persistHandle) return;
    this._persistHandle = persistState(messageStore, {
      key: tenantStorageKey(MESSAGES_STORE_NAME),
      storage: idbStorage,
    });
    // IDB preserves Date objects via structured clone — no rehydration coercion
    // required (unlike the previous localStorage path which JSON-serialized).
    this._persistHandle.initialized$.subscribe((ready) => {
      // Rehydration replaces ids/entities wholesale, so any prune that ran
      // before it landed is overwritten. Re-prune here, before signalling
      // readiness, so startup consumers (ChatService read-state seeding) see
      // post-retention state — this is what actually drops messages older
      // than the window when the app was closed longer than that.
      if (ready) this._pruneExpired();
      this.initialized$.next(ready);
    });
  }

  private _disablePersistence(): void {
    this._persistHandle?.unsubscribe();
    this._persistHandle = undefined;
    // Fire-and-forget — this deletion is not awaited or caught here. The adapter
    // rejects on storage failure; the in-memory store is untouched either way.
    idbStorage.removeItem(tenantStorageKey(MESSAGES_STORE_NAME));
    // In-memory store is intentionally untouched so messages currently visible
    // in the chat remain displayed; only the persisted history is dropped.
    // "initialized" still reflects the current session state.
    this.initialized$.next(true);
  }
}
